/**
 * Type-aware cell content for the explorer grid.
 */
import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { RecordStatusBadge } from '@/features/records/components/record-status-badge'
import type { RecordStatus } from '@/features/records/api/records-api'
import { formatCellValue, formatMetaValue } from '../lib/value-format'
import { SUBMISSION_STATUS_LABELS } from '../types/data-view-spec'
import type { ResolvedColumn, ResolvedRow } from '../lib/spec-resolver'

export function ExplorerCell({
  column, row, onOpenArray,
}: {
  column: ResolvedColumn
  row: ResolvedRow
  onOpenArray?: (column: ResolvedColumn, row: ResolvedRow) => void
}) {
  // Repeatable_array "separate" cell → clickable drilldown.
  if (column.kind === 'derived' && column.id in row.arrays && onOpenArray) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onOpenArray(column, row) }}
        className="text-primary hover:underline"
      >
        {row.display[column.id] ?? '—'}
      </button>
    )
  }
  // Pre-formatted derived / aggregate / array cells.
  if (column.kind === 'derived' || (column.id in row.display)) {
    return <span className="tabular-nums">{row.display[column.id] ?? '—'}</span>
  }

  if (column.kind === 'meta') {
    const key = column.metaKey!
    const val = row.cells[column.id]

    if (key === 'record_status') {
      return <RecordStatusBadge status={row.recordStatus as RecordStatus} />
    }
    if (key === 'submission_status') {
      return (
        <span className="text-xs text-muted-foreground">
          {SUBMISSION_STATUS_LABELS[String(val)] ?? (val ? String(val) : '—')}
        </span>
      )
    }
    if (key === 'soft_warning_count') {
      const n = Number(val ?? 0)
      if (!n) return <span className="text-muted-foreground">—</span>
      return (
        <span className="inline-flex items-center gap-1 text-amber-500" title={`${n} هشدار`}>
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="tabular-nums text-xs">{n}</span>
        </span>
      )
    }
    if (key === 'display_id') {
      return <span className="font-medium text-foreground whitespace-nowrap">{String(val ?? '—')}</span>
    }
    return <span className="tabular-nums">{formatMetaValue(key, val)}</span>
  }

  // scalar field (and client-computed calc) cells
  const display = formatCellValue(column.catalogField, row.cells[column.id])
  const numeric = column.fieldType === 'number' || column.kind === 'calc'
  return <span className={cn(numeric && 'tabular-nums')}>{display}</span>
}
